import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react"

type Theme = "dark" | "light" | "system"
type ResolvedTheme = "dark" | "light"

type ThemeProviderProps = {
  children: ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  isDark: (theme?: Theme) => boolean
}

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)"
const DEFAULT_THEME: Theme = "system"
const DEFAULT_STORAGE_KEY = "theme"
const THEME_VALUES: Theme[] = ["dark", "light", "system"]

export const ThemeProviderContext = createContext<ThemeProviderState | undefined>(undefined)

function isTheme(value: string | null): value is Theme {
  if (value === null) {
    return false
  }

  return THEME_VALUES.includes(value as Theme)
}

function getSystemTheme(): ResolvedTheme {
  if (window.matchMedia(COLOR_SCHEME_QUERY).matches) {
    return "dark"
  }

  return "light"
}

export function isDark(theme?: Theme): boolean {
  if (theme === undefined || theme === "system") {
    return getSystemTheme() === "dark"
  }

  return theme === "dark"
}

function getStoredTheme(storageKey: string, fallbackTheme: Theme): Theme {
  const storedTheme = localStorage.getItem(storageKey)
  if (isTheme(storedTheme)) {
    return storedTheme
  }

  return fallbackTheme
}

function applyTheme(nextTheme: Theme) {
  const root = document.documentElement
  const resolvedTheme = nextTheme === "system" ? getSystemTheme() : nextTheme

  root.classList.remove("light", "dark")
  root.classList.add(resolvedTheme)
  root.style.colorScheme = resolvedTheme
}

export function ThemeProvider({
  children,
  defaultTheme = DEFAULT_THEME,
  storageKey = DEFAULT_STORAGE_KEY,
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme(storageKey, defaultTheme))

  const setTheme = useCallback(
    (nextTheme: Theme) => {
      localStorage.setItem(storageKey, nextTheme)
      setThemeState(nextTheme)
    },
    [storageKey]
  )

  useEffect(() => {
    applyTheme(theme)

    if (theme !== "system") {
      return undefined
    }

    const mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY)
    const handleChange = () => {
      applyTheme("system")
    }

    mediaQuery.addEventListener("change", handleChange)

    return () => {
      mediaQuery.removeEventListener("change", handleChange)
    }
  }, [theme])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) {
        return
      }

      if (event.key !== storageKey) {
        return
      }

      if (isTheme(event.newValue)) {
        setThemeState(event.newValue)
        return
      }

      setThemeState(defaultTheme)
    }

    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [defaultTheme, storageKey])

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      isDark: (themeToCheck?: Theme) => isDark(themeToCheck ?? theme),
    }),
    [theme, setTheme]
  )

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

// Run during module evaluation so the html theme class is in place before React mounts.
// Relying on the effect alone is too late and causes a light-to-dark flash on reload when dark theme is selected.
applyTheme(getStoredTheme(DEFAULT_STORAGE_KEY, DEFAULT_THEME))
